// Per-level "name contains" filters — PURE + tested. Shared by BOTH reporting
// dashboards (Google: Campaign / Ad group / Ad · Meta: Campaign / Ad set / Ad).
//
// ── WHY THESE ARE DIFFERENT FROM THE ACCOUNT-LEVEL FILTERS ──────────────────
// Brand / Group / Industry / AM resolve through ATTRIBUTION (campaign-name token
// matching against the kyck roster), which is not a column and therefore has to be
// resolved in JS over the campaign directory. Entity NAMES are the opposite: they
// are real columns in the fact data at their own grain, so they belong in SQL as
// `LOWER(col) LIKE '%…%'`. Keeping the two apart matters — putting a name filter
// through attribution would be slow and wrong, and putting attribution into SQL
// would mean mirroring the roster into BigQuery.
//
// ── THE GRAIN RULE (the part that is easy to get wrong) ─────────────────────
// A name filter only matches at ITS OWN grain: a campaign-grain row carries no ad
// name, so `LOWER(ad_name) LIKE …` against campaign rows matches NOTHING and would
// silently zero the KPIs while the table still showed data. So the query grain has
// to follow the DEEPEST ACTIVE NAME FILTER — `deepestNameLevel` is that rule, and
// it is why the KPI/trend scope can equal the table scope rather than merely
// resembling it.
//
// ⚠️ Consequence, by design: an entity SURVIVES if it has at least one descendant
// matching every active filter, and the figures shown are then those descendants'.
// Filtering "Ad name contains Hero" narrows a campaign row to its Hero ads — it
// does not show the whole campaign just because one ad matched.
//
// ── EVERY LEVEL IS A TOKEN ARRAY ────────────────────────────────────────────
// Each level takes a LIST of "contains" tokens, ORed within the level and ANDed
// across levels. That is not a new invention: it is the Meta campaign-token
// search's existing behaviour (`applyCampaignTokenFilter`), lifted here so ad
// set / ad group / ad inherit it IDENTICALLY rather than each box meaning
// something subtly different from the one beside it.

/** The levels a name filter can target, shallowest → deepest. */
export type NameFilterLevel = 'campaign' | 'adGroup' | 'ad';

export const NAME_FILTER_LEVELS: NameFilterLevel[] = ['campaign', 'adGroup', 'ad'];

/**
 * TOKENS per level. Absent / empty = no constraint on that level.
 *
 * ⚠️ WITHIN a level the tokens are ORed, ACROSS levels they are ANDed. That
 * asymmetry is deliberate and is the Meta campaign-token search's existing
 * behaviour, generalised: several spellings of ONE thing ("Rosebay", "Rose Bay")
 * are alternatives, whereas a campaign filter and an ad filter are two separate
 * questions about the same row. Every level uses the IDENTICAL predicate — no
 * level invents its own semantics.
 */
export interface ReportingNameFilters {
  campaignName?: string[];
  adGroupName?: string[];
  adName?: string[];
}

/**
 * A level's cleaned tokens: trimmed, lowercased, blanks dropped. Empty = not
 * constraining.
 *
 * ⚠️ The cleaning is EXACTLY what applyCampaignTokenFilter has always done to
 * the Meta campaign tokens, because these are now the same feature — matching it
 * by construction is what keeps that behaviour byte-identical.
 */
export function nameTokens(
  filters: ReportingNameFilters | undefined,
  level: NameFilterLevel,
): string[] {
  if (!filters) return [];
  const raw =
    level === 'campaign'
      ? filters.campaignName
      : level === 'adGroup'
        ? filters.adGroupName
        : filters.adName;
  return (raw ?? []).map((t) => t.trim().toLowerCase()).filter((t) => t !== '');
}

/** Every level that is actually constraining, shallowest → deepest. */
export function activeNameLevels(filters: ReportingNameFilters | undefined): NameFilterLevel[] {
  return NAME_FILTER_LEVELS.filter((l) => nameTokens(filters, l).length > 0);
}

export function hasNameFilters(filters: ReportingNameFilters | undefined): boolean {
  return activeNameLevels(filters).length > 0;
}

/**
 * The DEEPEST constraining level — the grain a query must run at for the filters
 * to match anything at all. null when nothing is constraining.
 */
export function deepestNameLevel(
  filters: ReportingNameFilters | undefined,
): NameFilterLevel | null {
  const active = activeNameLevels(filters);
  return active.length > 0 ? active[active.length - 1]! : null;
}

/**
 * True when a filter targets a level BELOW the campaign — the case that forces a
 * deeper query grain (and, on Google, drops impression share to "not measured",
 * because share is a campaign-level concept and a subset of ads has none).
 */
export function hasSubCampaignNameFilter(filters: ReportingNameFilters | undefined): boolean {
  return activeNameLevels(filters).some((l) => l !== 'campaign');
}

/**
 * Build the SQL predicates + bound params for the active name filters.
 *
 * ⚠️ The tokens are ALWAYS a bound ARRAY parameter, never interpolated — a user
 * types these, and `%`/`_` inside one are treated as literal text by `STRPOS`
 * rather than as LIKE wildcards, so searching for "50%_off" finds "50%_off"
 * instead of matching half the account. That is why this uses STRPOS, not LIKE.
 *
 * ⚠️ The emitted shape is the EXACT predicate the Meta campaign-token search has
 * always used (`EXISTS … UNNEST … STRPOS`) — one clause per level, ORing within
 * it. Reusing the shape rather than inventing a per-level one is the whole point:
 * "several campaign tokens" and "several ad tokens" now mean the same thing.
 *
 * `columns` maps a level to the column name at the query's grain. A level with no
 * column mapping is SKIPPED rather than silently dropped into a false predicate —
 * callers only pass columns that exist at the grain they are building for.
 */
export function buildNameFilterClauses(
  filters: ReportingNameFilters | undefined,
  columns: Partial<Record<NameFilterLevel, string>>,
  opts: { paramPrefix?: string } = {},
): { clauses: string[]; params: Record<string, string[]> } {
  const prefix = opts.paramPrefix ?? 'name';
  const clauses: string[] = [];
  const params: Record<string, string[]> = {};

  for (const level of NAME_FILTER_LEVELS) {
    const tokens = nameTokens(filters, level);
    if (tokens.length === 0) continue;
    const column = columns[level];
    if (!column) continue;
    const param = `${prefix}${level[0]!.toUpperCase()}${level.slice(1)}`;
    clauses.push(
      `EXISTS (SELECT 1 FROM UNNEST(@${param}) t WHERE t != '' AND STRPOS(LOWER(${column}), t) > 0)`,
    );
    params[param] = tokens;
  }

  return { clauses, params };
}

/**
 * The client-side equivalent, for filtering an already-fetched directory (Google
 * resolves the campaign-name filter in JS over its campaign directory, because
 * that is where campaign identity already lives).
 *
 * Empty tokens = no constraint; otherwise ANY token matching passes — the same
 * OR the SQL predicate above expresses.
 */
export function matchesNameTokens(
  value: string | null | undefined,
  tokens: readonly string[],
): boolean {
  if (tokens.length === 0) return true;
  const haystack = (value ?? '').toLowerCase();
  return tokens.some((t) => haystack.includes(t));
}

/**
 * Pick the deeper of two levels — used to resolve a query grain from BOTH the
 * displayed levels and the active name filters.
 */
export function deeperLevel(a: NameFilterLevel, b: NameFilterLevel): NameFilterLevel {
  return NAME_FILTER_LEVELS.indexOf(a) >= NAME_FILTER_LEVELS.indexOf(b) ? a : b;
}
